import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Env } from "../../env";

export interface GlobalSettings {
    disableVideos?: boolean;
    showTimers?: boolean;
    showLogs?: boolean;
    enableSttDiagnostics?: boolean;
    enableAndroidNativeRecorder?: boolean;
    enableAndroidAudioConstraints?: boolean;
    enableAssessmentWakeLock?: boolean;
    isCheckout?: boolean;
    presentTargets: number;
    cuedRecall: number;
    freeRecall: number;
    freeRecallTimeout: number;
    delayTime: number;
    assessmentTimeout: number;
    sttBoost: number;
    ptTimeout: number;
    frTimeout: number;
    crTimeout: number;
    googleSttKey: string;
    azureSttKey: string;
    azureSttRegion: string;
}

export const initialState: GlobalSettings = {
    disableVideos: false,
    showTimers: Env.REACT_APP_SHOW_TIMERS === "true",
    showLogs: Env.REACT_APP_SHOW_LOGS === "true",
    enableSttDiagnostics: Env.REACT_APP_ENABLE_STT_DIAGNOSTICS === "true",
    enableAndroidNativeRecorder: Env.REACT_APP_ENABLE_ANDROID_NATIVE_RECORDER === "true",
    enableAndroidAudioConstraints: Env.REACT_APP_ENABLE_ANDROID_AUDIO_CONSTRAINTS !== "false",
    enableAssessmentWakeLock: Env.REACT_APP_ENABLE_ASSESSMENT_WAKE_LOCK !== "false",
    sttBoost: Number(Env.REACT_APP_STT_BOOST) || 7,
    ptTimeout: Number(Env.REACT_APP_STT_PT_TIMEOUT) || 10,
    frTimeout: Number(Env.REACT_APP_STT_FR_TIMEOUT) || 30,
    crTimeout: Number(Env.REACT_APP_STT_CR_TIMEOUT) || 25,
    googleSttKey: Env.REACT_APP_GOOGLE_STT_KEY || "",
    azureSttKey: Env.REACT_APP_AZURE_STT_KEY || "",
    azureSttRegion: Env.REACT_APP_AZURE_STT_REGION || "",
    presentTargets: Number(Env.REACT_APP_PRESENT_TARGETS_TIME) || 6,
    cuedRecall: Number(Env.REACT_APP_CUED_RECALL_TIME) || 20,
    freeRecall: Number(Env.REACT_APP_FREE_RECALL_TIME) || 60,
    freeRecallTimeout: Number(Env.REACT_APP_FREE_RECALL_TIMEOUT) || 10,
    delayTime: Number(Env.REACT_APP_DELAY_TIME) || 60,
    assessmentTimeout: Number(Env.REACT_APP_ASSESSMENT_TIMEOUT) || 120,
    isCheckout: false
};

const checkExistentAndIsNumber = (params: { [key: string]: string }, field: string): boolean => {
    return !!params[field] && !isNaN(Number(params[field]));
};

const globalSettingsSlice = createSlice({
    name: "globalSettings",
    initialState,
    reducers: {
        updateGlobalSettings(state, action: PayloadAction<{ [key: string]: string }>) {
            const params = action.payload;

            if (params.disableVideos) {
                state.disableVideos = params.disableVideos === "true";
            }

            if (params.showTimers) {
                state.showTimers = params.showTimers === "true";
            }

            if (params.enableSttDiagnostics) {
                state.enableSttDiagnostics = params.enableSttDiagnostics === "true";
            }

            if (params.enableAndroidNativeRecorder) {
                state.enableAndroidNativeRecorder = params.enableAndroidNativeRecorder === "true";
            }

            if (params.enableAndroidAudioConstraints) {
                state.enableAndroidAudioConstraints = params.enableAndroidAudioConstraints === "true";
            }

            if (params.enableAssessmentWakeLock) {
                state.enableAssessmentWakeLock = params.enableAssessmentWakeLock === "true";
            }

            if (params.isCheckout) {
                state.isCheckout = params.isCheckout === "true";
            }

            if (checkExistentAndIsNumber(params, "presentTargets")) {
                state.presentTargets = Number(params.presentTargets);
            }
            if (checkExistentAndIsNumber(params, "cuedRecall")) {
                state.cuedRecall = Number(params.cuedRecall);
            }
            if (checkExistentAndIsNumber(params, "freeRecall")) {
                state.freeRecall = Number(params.freeRecall);
            }
            if (checkExistentAndIsNumber(params, "freeRecallTimeout")) {
                state.freeRecallTimeout = Number(params.freeRecallTimeout);
            }
            if (checkExistentAndIsNumber(params, "delayTime")) {
                state.delayTime = Number(params.delayTime);
            }
        },
        reinstateGlobalSettings(state, action: PayloadAction<GlobalSettings>) {
            state = action.payload;
        }
    }
});

export const { updateGlobalSettings, reinstateGlobalSettings } = globalSettingsSlice.actions;

export default globalSettingsSlice.reducer;
